'use client';

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { buildTableHref, type TableQuery } from '@/lib/table-sorting';

export type PaginationControlsQuery = TableQuery;

export function PaginationControls({
  page,
  pageCount,
  basePath,
  queryParam = 'page',
  query,
}: {
  page: number;
  pageCount: number;
  basePath: string;
  queryParam?: string;
  query?: PaginationControlsQuery;
}) {
  if (pageCount <= 1) return null;

  const prevPage = Math.max(page - 1, 1);
  const nextPage = Math.min(page + 1, pageCount);

  const buildHref = (targetPage: number) => {
    return buildTableHref({
      basePath,
      query,
      overrides: {
        [queryParam]: targetPage,
      },
    });
  };

  const getPagesToShow = () => {
    const pages: (number | 'ellipsis')[] = [];

    if (pageCount <= 7) {
      // Show all pages if count is small
      for (let i = 1; i <= pageCount; i++) pages.push(i);
    } else {
      pages.push(1);

      if (page > 4) pages.push('ellipsis');

      const start = Math.max(2, page - 1);
      const end = Math.min(pageCount - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < pageCount - 3) pages.push('ellipsis');

      pages.push(pageCount);
    }

    return pages;
  };

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious disabled={page === 1} href={buildHref(prevPage)} />
        </PaginationItem>

        {getPagesToShow().map((item, index) =>
          item === 'ellipsis' ? (
            <PaginationItem key={`ellipsis-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ) : (
            <PaginationItem key={item}>
              <PaginationLink href={buildHref(item)} isActive={item === page}>
                {item}
              </PaginationLink>
            </PaginationItem>
          )
        )}

        <PaginationItem>
          <PaginationNext disabled={page === pageCount} href={buildHref(nextPage)} />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
